<template>
  <div class="mood-selector">
    <!-- Encabezado con icono opcional de gráfica -->
    <div class="d-flex justify-content-center align-items-center mb-4">
      <h3 class="mb-0">{{ $t('tests.subtitleMoods') }}</h3>
      <base-icon
        v-if="showGraphIcon"
        class="clickable icon ml-2"
        src="/static-appopensalud/img/icons/morillas-svg-icons/primary/graph-icon-blue.svg"
        @click="openGraphModal"
      />
    </div>

    <div class="mood-list">
      <div
        v-for="mood in allMoods"
        :key="mood.value"
        class="mood-item"
        :class="{
          selected: mood.value === selectedMoodCode,
          'read-only': readOnly,
          dimmed: readOnly && mood.value !== selectedMoodCode
        }"
        :style="{ backgroundColor: itemColor }"
        @click="selectMood(mood)"
      >
        <span
          class="mood-icon"
          :style="getIconStyle(mood.typeMood)"
        ></span>
        <span class="mood-name">{{ mood.name }}</span>
      </div>
    </div>

    <!-- Modal con MoodTracker -->
    <base-modal
      v-if="showGraphIcon"
      v-model="showGraphModal"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
    >
      <mood-tracker :moodData="allHistoryMoods" />
    </base-modal>
  </div>
</template>

<script>
import { WHITE_COLOUR, RED_COLOUR, DARK_YELLOW_COLOUR, GREEN_COLOUR } from '@/constants/colours'
import ClinicHistoryRepository from '@/repositories/ClinicHistoryRepository'
import typeMood from '@/constants/typeMood'

import BaseModal from '@/components/base/BaseModal.vue'
import MoodTracker from '@/components/views/selfEvaluationTests/mood/MoodTracker.vue'

function getEmojiSrc (fileName) {
  const ROOT_RELATIVE_PATH = '/static-appopensalud/img/icons/emojis/'
  return ROOT_RELATIVE_PATH + fileName
}
const tickIcons = {
  1: { src: getEmojiSrc('emoji-very-sad.svg'), color: RED_COLOUR },
  2: { src: getEmojiSrc('emoji-no-feelings.svg'), color: DARK_YELLOW_COLOUR },
  3: { src: getEmojiSrc('emoji-happy.svg'), color: GREEN_COLOUR }
}

export default {
  name: 'MoodSelector',
  components: { BaseModal, MoodTracker },
  props: {
    itemColor: {
      type: String,
      default: WHITE_COLOUR
    },
    initialSelectedMoodCode: Number,
    readOnly: {
      type: Boolean,
      default: false
    },
    showGraphIcon: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      selectedMoodCode: this.initialSelectedMoodCode || null,
      allMoods: [],
      showGraphModal: false,
      allHistoryMoods: []
    }
  },
  methods: {
    /** Devuelve el estilo inline para la carita del mood */
    getIconStyle (type) {
      const icon = tickIcons[type]
      if (!icon) return {}
      return {
        backgroundColor: icon.color,
        WebkitMask: `url('${icon.src}') center/contain no-repeat`,
        mask: `url('${icon.src}') center/contain no-repeat`
      }
    },

    selectMood (moodObj) {
      if (this.readOnly) return
      this.selectedMoodCode = moodObj.value

      ClinicHistoryRepository()
        .saveMood({ idMood: this.selectedMoodCode })
        .then(() => {
          if (moodObj.typeMood === typeMood.POSITIVE) {
            this.$store.dispatch(
              'showSuccessAlert',
              this.$t('tests.today-health-condition-saved-nice')
            )
          } else if (moodObj.typeMood === typeMood.NEGATIVE) {
            this.$store.dispatch(
              'showSuccessAlert',
              this.$t('tests.today-health-condition-saved-ok')
            )
          } else {
            this.$store.dispatch(
              'showSuccessAlert',
              this.$t('tests.today-health-condition-saved-bad')
            )
          }
          this.$emit('mood-selected', true)
        })
    },

    /** Abre el modal y carga el histórico de moods */
    openGraphModal () {
      this.showGraphModal = true

      ClinicHistoryRepository()
        .getHistoryMoods()
        .then((resp) => {
          if (resp.data) {
            this.allHistoryMoods = resp.data.map((m) => ({
              date: m.date.split('T')[0],
              name: this.$i18nHelpers.normalize(m.name),
              typeMood: m.typeMood
            }))
          } else {
            this.allHistoryMoods = []
          }
        })
        .catch((err) => {
          console.error('Error al cargar historial de moods:', err)
          this.allHistoryMoods = []
        })
    },

    selected (code) {
      if (!code || !this.allMoods.length) return
      const found = this.allMoods.find((m) => m.value === code)
      if (found) this.selectedMoodCode = found.value
    },

    loadMoods () {
      ClinicHistoryRepository()
        .getAllMoods()
        .then((item) => {
          if (item.data) {
            this.allMoods = item.data.map((m) => ({
              value: m.value,
              name: this.$i18nHelpers.normalize(m.name),
              typeMood: m.typeMood,
              emojiCode: m.emojiCode
            }))
            this.selected(this.initialSelectedMoodCode)
          }
        })
        .catch((err) => {
          console.error('Error cargando moods:', err)
        })
    }
  },

  mounted () {
    this.loadMoods()
  },

  watch: {
    initialSelectedMoodCode (val) {
      this.selected(val)
    }
  }
}
</script>

<style lang="scss" scoped>
.mood-selector {
  margin: 10px 0;
}

.mood-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 1em;
  justify-content: center;
}

.mood-item {
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 16px;
  transition: transform 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 72px;
}

.mood-icon {
  width: 18px;
  height: 18px;
  display: inline-block;
}

.mood-item.selected {
  transform: scale(1.05);
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
  opacity: 1;
}

.mood-name {
  font-size: 0.85rem;
}

.mood-item.read-only {
  cursor: default;
  pointer-events: none;
}

.mood-item.dimmed {
  opacity: 0.35;
}
</style>



// WEBPACK FOOTER //
// src/components/views/selfEvaluationTests/mood/MoodSelector.vue